import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRefs, watch } from 'vue';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import type {
  CollectionContentType,
  ContentCollection,
  ContentReference,
} from '@ilteducation/content-collections-types';
import eventBus from '@/foundations/services/eventBus';
import { addContent, getList, removeContent } from '@/data-provider/contentCollections';

// CollectionContentType is a TS enum, so a plain `content-type="Book"` attribute isn't
// assignable to it. Widen to the enum's string union for the prop and cast at the two
// points where it crosses into the API payload — no runtime dependency on the enum object.
type ContentTypeName = `${CollectionContentType}`;

type Props = {
  contentType: ContentTypeName;
  ids: string[];
  subscriptionType: string[];
  availabilityRealms: string[];
};

export default /*#__PURE__*/_defineComponent({
  __name: 'ContentToCollection',
  props: {
    contentType: null,
    ids: null,
    subscriptionType: null,
    availabilityRealms: null
  },
  setup(__props: any) {

const props = __props as {
  contentType: ContentTypeName;
  ids: string[];
  subscriptionType: string[];
  availabilityRealms: string[];
}


const { contentType, ids, subscriptionType, availabilityRealms } = toRefs(props);

const items = ref<ContentCollection[]>([]);
const isLoading = ref<boolean>(false);
const selectedRealm = ref<string | undefined>(undefined);
const selectedContext = ref<string | undefined>(undefined);
const selectedAddCollection = ref<ContentCollection | undefined>(undefined);
const selectedRemoveCollection = ref<ContentCollection | undefined>(undefined);

const availableMarkets = computed(() => defaultRealms.filter((realm) => availabilityRealms.value.includes(realm.code)));
const availableContexts = computed(() =>
  defaultSubscriptions.filter((subscription) => subscriptionType.value.includes(subscription.value)),
);

const refType = computed(() => contentType.value as CollectionContentType);

// CollectionContents is a union — query-based collections have no editable reference list,
// so they read as empty and are left untouched on write.
const referencesOf = (collection: ContentCollection): ContentReference[] =>
  collection.contents?.type === 'ContentsByReferences' ? collection.contents.references : [];

const withReferences = (collection: ContentCollection, references: ContentReference[]): ContentCollection =>
  collection.contents?.type === 'ContentsByReferences'
    ? { ...collection, contents: { ...collection.contents, references } }
    : collection;

const isReferenced = (collection: ContentCollection, id: string) =>
  referencesOf(collection).some((ref) => ref.id === id && ref.type === refType.value);

const toOption = (item: ContentCollection) => ({ text: item.name, id: item.id, data: item });

const addItems = computed(() =>
  items.value?.filter((item) => !ids.value.every((id) => isReferenced(item, id))).map(toOption),
);

const removeItems = computed(() =>
  items.value?.filter((item) => ids.value.some((id) => isReferenced(item, id))).map(toOption),
);

const fetchItems = async () => {
  // Reset both: leaving them stale made a second market/context selection re-append page 1
  // on top of the previous market's results, so the dropdowns filled with duplicates.
  items.value = [];
  let cursor: string | null = null;
  try {
    isLoading.value = true;
    do {
      const filter = { realm: selectedRealm.value, context: selectedContext.value };
      // eslint-disable-next-line no-await-in-loop
      const page = await getList(filter, cursor);
      items.value = [...items.value, ...page.data];
      cursor = typeof page.cursor === 'string' ? page.cursor : null;
    } while (cursor && cursor.length > 0);
  } catch (error) {
    console.error(error);
    eventBus.$emit('snackbarV2.open', { text: `Failed to load collections!`, color: 'error' });
  } finally {
    isLoading.value = false;
  }
};

watch([selectedRealm, selectedContext], async () => {
  if (selectedRealm.value && selectedContext.value) {
    await fetchItems();
  }
});

/**
 * Sequential and reversed on purpose: the /contents endpoint takes no position and prepends
 * (ContentsService.addContent unshifts), so the last call ends up first. Walking the
 * selection backwards leaves the collection in selection order.
 */
const runOnEach = async (action: (id: string) => Promise<void>): Promise<string[]> => {
  const failed: string[] = [];
  for (const id of [...ids.value].reverse()) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await action(id);
    } catch (error) {
      console.error(error);
      failed.push(id);
    }
  }
  return failed;
};

const report = (verb: string, failed: string[]) => {
  const done = ids.value.length - failed.length;
  eventBus.$emit(
    'snackbarV2.open',
    failed.length
      ? { text: `${verb} ${done}/${ids.value.length} — failed: ${failed.join(', ')}`, color: 'error' }
      : { text: `${verb} ${done} ${contentType.value}${done === 1 ? '' : 's'}`, color: 'success' },
  );
};

const onAdd = async () => {
  const collection = selectedAddCollection.value;
  const realm = selectedRealm.value;
  if (!collection || !realm) {
    return;
  }
  isLoading.value = true;
  const failed = await runOnEach((id) => addContent(realm, collection.id, { type: refType.value, id }));
  const added = ids.value.filter((id) => !failed.includes(id));
  items.value = items.value.map((item) =>
    item.id === collection.id
      ? withReferences(item, [
          ...added.filter((id) => !isReferenced(item, id)).map((id) => ({ id, type: refType.value })),
          ...referencesOf(item),
        ])
      : item,
  );
  report('Added', failed);
  isLoading.value = false;
  selectedAddCollection.value = undefined;
};

const onRemove = async () => {
  const collection = selectedRemoveCollection.value;
  const realm = selectedRealm.value;
  if (!collection || !realm) {
    return;
  }
  isLoading.value = true;
  const failed = await runOnEach((id) => removeContent(realm, collection.id, { type: refType.value, id }));
  const removed = ids.value.filter((id) => !failed.includes(id));
  items.value = items.value.map((item) =>
    item.id === collection.id
      ? withReferences(
          item,
          referencesOf(item).filter((ref) => !(removed.includes(ref.id) && ref.type === refType.value)),
        )
      : item,
  );
  report('Removed', failed);
  isLoading.value = false;
  selectedRemoveCollection.value = undefined;
};

return { __sfc: true,props, contentType, ids, subscriptionType, availabilityRealms, items, isLoading, selectedRealm, selectedContext, selectedAddCollection, selectedRemoveCollection, availableMarkets, availableContexts, refType, referencesOf, withReferences, isReferenced, toOption, addItems, removeItems, fetchItems, runOnEach, report, onAdd, onRemove }
}

})